import { useState, useCallback, useRef } from 'react';
import { Map as OlMap } from 'ol';

/**
 * @author Kim Young Jun
 * @date 2025-09-08
 * 경사도 분석 Hook
 * Map.tsx의 기존 경사도 함수들을 활용하여 상태 관리를 독립화한 커스텀 Hook
 */
export const useSlopeAnalysis = () => {
    const [isSlopeActive, setIsSlopeActive] = useState(false);
    const mapRef = useRef<OlMap | null>(null);
    const slopeStartFunctionRef = useRef<((map: OlMap) => void) | null>(null);
    const slopeStopFunctionRef = useRef<((map: OlMap) => void) | null>(null);

    /**
     * Map 인스턴스와 경사도 함수들을 설정
     * Map.tsx에서 호출하여 함수 참조를 전달받음
     */
    const initialize = useCallback((
        map: OlMap | null, 
        startFn?: (map: OlMap) => void,
        stopFn?: (map: OlMap) => void
    ) => {
        mapRef.current = map;
        if (startFn) slopeStartFunctionRef.current = startFn;
        if (stopFn) slopeStopFunctionRef.current = stopFn;
    }, []);

    /**
     * 경사도 분석 시작
     */
    const startSlopeAnalysis = useCallback((map?: OlMap) => {
        const targetMap = map || mapRef.current;
        if (!targetMap || !slopeStartFunctionRef.current) return;

        slopeStartFunctionRef.current(targetMap);
        setIsSlopeActive(true);
    }, []);

    /**
     * 경사도 분석 종료
     */
    const stopSlopeAnalysis = useCallback((map?: OlMap) => {
        const targetMap = map || mapRef.current;
        if (!targetMap || !slopeStopFunctionRef.current) return;

        slopeStopFunctionRef.current(targetMap);
        setIsSlopeActive(false);
    }, []);

    /**
     * 경사도 분석 토글
     */
    const toggleSlopeAnalysis = useCallback((map?: OlMap) => {
        if (isSlopeActive) {
            stopSlopeAnalysis(map);
        } else {
            startSlopeAnalysis(map);
        }
    }, [isSlopeActive, startSlopeAnalysis, stopSlopeAnalysis]);

    /**
     * 경사도 분석 강제 리셋 (충돌 방지용)
     */
    const resetSlopeAnalysis = useCallback((map?: OlMap) => {
        if (isSlopeActive) {
            stopSlopeAnalysis(map);
        }
    }, [isSlopeActive, stopSlopeAnalysis]);

    return {
        isSlopeActive,
        initialize,
        startSlopeAnalysis,
        stopSlopeAnalysis,
        toggleSlopeAnalysis,
        resetSlopeAnalysis
    };
};